import React from 'react';
import axios, { AxiosInstance, AxiosError } from 'axios';
import { logger } from '../utils/logger';
import { useAuth } from '@clerk/clerk-react';

// Import types from existing API file
export * from './api';

// ==================== API CLIENT SINGLETON ====================

class ClerkApiClient {
  private static instance: ClerkApiClient;
  private axios: AxiosInstance;
  private getToken: (() => Promise<string | null>) | null = null;
  
  private constructor() {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    
    logger.info('Clerk API Client initialized', {
      baseURL,
      hasEnvVar: !!process.env.REACT_APP_API_URL,
      hostname: window.location.hostname
    });
    
    this.axios = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    this.setupInterceptors();
  }
  
  static getInstance(): ClerkApiClient {
    if (!ClerkApiClient.instance) {
      ClerkApiClient.instance = new ClerkApiClient();
    }
    return ClerkApiClient.instance;
  }

  // Set the Clerk getToken function
  setTokenGetter(getToken: () => Promise<string | null>): void {
    this.getToken = getToken;
  }
  
  private setupInterceptors(): void {
    // Request interceptor for auth token
    this.axios.interceptors.request.use(
      async (config) => {
        if (this.getToken) {
          try {
            const token = await this.getToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
              logger.debug('[Clerk API] Added auth token to request', { url: config.url });
            } else {
              // eslint-disable-next-line no-console
              console.warn('[Clerk API] No token available for request:', config.url);
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('[Clerk API] Failed to get token:', error);
            logger.error('Failed to get Clerk token', error as Error);
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn('[Clerk API] Token getter not set for request:', config.url);
        }
        
        logger.debug('API Request', {
          method: config.method,
          url: config.url,
          hasAuth: !!config.headers.Authorization
        });
        
        return config;
      },
      (error) => {
        logger.error('Request interceptor error', error);
        return Promise.reject(error);
      }
    );
    
    // Response interceptor for error handling
    this.axios.interceptors.response.use(
      (response) => {
        logger.debug('API Response', {
          status: response.status,
          url: response.config.url
        });
        return response;
      },
      async (error: AxiosError) => {
        // Handle 401 errors
        if (error.response?.status === 401) {
          logger.warn('Unauthorized request - NOT REDIRECTING TO PREVENT LOOP');
          console.error('[Clerk API Client] 401 Error - Check authentication state');
          // DON'T REDIRECT - just reject the error
          return Promise.reject(error);
        }
        
        // Log error details
        logger.error('API Error', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.message,
          data: error.response?.data
        });
        
        throw error;
      }
    );
  }
  
  // HTTP methods
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.axios.get<any>(url, { params });
    return response.data.data || response.data;
  }
  
  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.axios.post<any>(url, data);
    return response.data.data || response.data;
  }
  
  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.axios.put<any>(url, data);
    return response.data.data || response.data;
  }
  
  async delete<T>(url: string): Promise<T> {
    const response = await this.axios.delete<any>(url);
    return response.data.data || response.data;
  }
  
  // Get the base URL
  getBaseUrl(): string {
    return this.axios.defaults.baseURL || '';
  }
}

// Export singleton instance
export const clerkApiClient = ClerkApiClient.getInstance();

// Hook to use with Clerk
export function useClerkApiClient() {
  const { getToken } = useAuth();
  
  // Set the token getter when the component mounts
  React.useEffect(() => {
    clerkApiClient.setTokenGetter(getToken);
  }, [getToken]);
  
  return clerkApiClient;
}